import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { formatUSD, type CostBreakdown } from "../../analytics";

/* Cost by token class — the Figma overview's radial tick ring (node 284:621):
 * title + big light total top-left, the mono legend bottom-left, and a
 * monochrome spoke ring on the right whose focused class reads dark while the
 * rest stay muted. Hover (ring or legend) moves the focus; at rest the
 * largest class is focused. Focus changes travel: a highlight wavefront sets
 * off from the previous focus's trailing edge and rotates the SHORT way
 * around the ring to the new focus, un-lighting the old segment and lighting
 * the new one tick by tick. */
export function CostByTokenClass({
  cost,
  onMore,
}: {
  cost: CostBreakdown;
  onMore?: () => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const focus = hover === null ? 0 : hover;

  const segs = (
    [
      { name: "Cache write", cost: cost.cache_write },
      { name: "Cache read", cost: cost.cache_read },
      { name: "Output", cost: cost.output },
      { name: "Fresh input", cost: cost.input },
    ] as const
  )
    .filter((s) => s.cost > 0)
    .sort((a, b) => b.cost - a.cost);

  const total = segs.reduce((s, d) => s + d.cost, 0) || 1;
  const pctOf = (d: { cost: number }) => (d.cost / total) * 100;

  // Ring geometry per the Figma Icon (node 284:650): a dense ring of 100
  // equal-length hairline ticks; only the color separates the focused class
  // from the muted rest.
  const N = 100,
    cx = 100,
    cy = 100,
    ri = 70,
    ro = 94;
  let acc = 0;
  const bounds = segs.map((d) => {
    acc += d.cost / total;
    return acc;
  });
  const classOf = (frac: number) => {
    for (let i = 0; i < bounds.length; i++) if (frac <= bounds[i]) return i;
    return segs.length - 1;
  };
  const spokes = Array.from({ length: N }).map((_, i) => {
    const frac = (i + 0.5) / N;
    const ci = classOf(frac);
    const ang = ((-90 + (i / N) * 360) * Math.PI) / 180;
    return {
      ci,
      frac,
      x1: cx + ri * Math.cos(ang),
      y1: cy + ri * Math.sin(ang),
      x2: cx + ro * Math.cos(ang),
      y2: cy + ro * Math.sin(ang),
    };
  });

  // ── Focus-travel wave ──────────────────────────────────────────────────────
  // On focus change, a highlight head travels the ring from the previous
  // focus's trailing edge to the new focus's far edge, taking the shorter way
  // around. Behind the head the new state applies (destination lit,
  // rest muted); ahead of it the old state persists; the head itself glows
  // over whatever it's crossing (the 120ms stroke transition leaves a fading
  // tail). Held in state and computed declaratively per render, so an
  // interrupted or re-fired wave can never leave stale strokes behind.
  const FULL_TURN_MS = 450;
  const HEAD = 0.08;
  const [wave, setWave] = useState<{
    from: number;
    to: number;
    clockwise: boolean;
    origin: number;
    span: number;
    /** Traveled ring-fraction so far (0..span). */
    t: number;
  } | null>(null);

  const prevFocusRef = useRef(focus);
  useLayoutEffect(() => {
    const prev = prevFocusRef.current;
    if (prev === focus) return;

    prevFocusRef.current = focus;
    if (
      bounds.length === 0 ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      setWave(null);
      return;
    }

    const from = Math.min(prev, bounds.length - 1);
    // It's a circle — travel whichever way around is shorter (the last
    // segment's end sits right next to the first one's start). Each candidate
    // sets off from the previous segment's trailing edge for its direction
    // and stops at the destination's far edge; pick the smaller trip.
    const candidate = (clockwise: boolean) => {
      const origin = clockwise
        ? from === 0
          ? 0
          : bounds[from - 1]
        : bounds[from];
      const distOf = (frac: number) =>
        clockwise ? (frac - origin + 1) % 1 : (origin - frac + 1) % 1;
      let span = 0;
      for (let i = 0; i < N; i++) {
        const frac = (i + 0.5) / N;
        if (classOf(frac) === focus) span = Math.max(span, distOf(frac));
      }
      return { clockwise, origin, span };
    };
    const cw = candidate(true);
    const ccw = candidate(false);
    const dir = cw.span <= ccw.span ? cw : ccw;
    setWave({ from, to: focus, ...dir, t: 0 });
    // bounds/classOf re-derive from props; focus is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // Advance the wavefront one frame at a time until it lands.
  useEffect(() => {
    if (!wave || wave.t >= wave.span) return;

    const id = requestAnimationFrame(() =>
      setWave((w) => {
        if (!w) return w;

        const nt = w.t + 16.7 / FULL_TURN_MS;
        return { ...w, t: Math.min(nt, w.span) };
      }),
    );
    return () => cancelAnimationFrame(id);
  }, [wave]);

  /** Whether a tick reads dark right now (wave in flight or resting state). */
  const isDark = (frac: number, ci: number) => {
    if (wave && wave.t < wave.span && wave.to === focus) {
      const d = wave.clockwise
        ? (frac - wave.origin + 1) % 1
        : (wave.origin - frac + 1) % 1;
      if (d > wave.t) return ci === wave.from;

      return ci === wave.to || d > wave.t - HEAD;
    }

    return ci === focus;
  };

  // Zero-traffic workspace: every class filters out, leaving no segments. The
  // ring + legend index into segs, so render an inline empty state instead of
  // dereferencing segs[0] (which crashed the whole overview via the boundary).
  // (After the hooks — they must run unconditionally.)
  if (segs.length === 0) {
    return (
      <div className="flex h-[336px] flex-col rounded-[5px] border border-[var(--border)] px-5 py-[18px]">
        <p className="cursor-default text-[15px] leading-none text-[var(--text)]">
          Cost by token type
        </p>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-[12px] text-[var(--text-muted)]">
            No spend in this window yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="tw-float-up flex h-[336px] items-stretch justify-between gap-4 rounded-[5px] border border-[var(--border)] px-5 py-[18px]"
      style={{ "--float-delay": "0.38s" } as CSSProperties}
    >
      <div className="flex min-w-0 flex-col">
        <p className="cursor-default text-[15px] leading-none text-[var(--text)]">
          Cost by token type
        </p>
        <div className="mt-3 text-[40px] font-light leading-none tracking-tight tabular-nums text-[var(--text)]">
          {formatUSD(total)}
        </div>
        {onMore && (
          <button
            onClick={onMore}
            className="mt-2 self-start text-[10px] text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
          >
            View more →
          </button>
        )}
        <div className="mt-auto flex flex-col gap-2">
          {segs.map((d, i) => {
            const focused = hover === null ? i === 0 : hover === i;
            return (
              <div
                key={i}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                className="tw-mono flex cursor-default items-center gap-2 text-[9px] leading-none"
                style={{
                  opacity: focused ? 1 : 0.5,
                  transition: "opacity 120ms ease",
                }}
              >
                <span
                  className="size-[7px] flex-shrink-0"
                  style={{
                    background: focused ? "var(--text)" : "var(--text-muted)",
                  }}
                />
                <span
                  className="w-[120px]"
                  style={{
                    color: focused ? "var(--text)" : "var(--text-muted)",
                  }}
                >
                  <span style={{ color: "var(--text-muted)" }}>{d.name}</span>{" "}
                  <span className="tabular-nums">{pctOf(d).toFixed(1)}%</span>
                </span>
                <span
                  className="tabular-nums"
                  style={{
                    color: focused ? "var(--text)" : "var(--text-muted)",
                  }}
                >
                  {formatUSD(d.cost)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center">
        <svg
          viewBox="0 0 200 200"
          className="h-full max-h-[260px] w-auto max-w-[260px]"
          onMouseLeave={() => setHover(null)}
        >
          {spokes.map((s, i) => (
            <line
              key={i}
              className="tw-spoke-in"
              x1={s.x1}
              y1={s.y1}
              x2={s.x2}
              y2={s.y2}
              onMouseEnter={() => setHover(s.ci)}
              stroke={
                isDark(s.frac, s.ci) ? "var(--text)" : "var(--spoke-mute)"
              }
              strokeWidth="1.4"
              style={
                {
                  // Also softens the wave: passed ticks fade back over 120ms,
                  // trailing the head like a comet tail.
                  transition: "stroke 120ms ease",
                  cursor: "pointer",
                  // Clockwise entrance sweep, after the card floats in.
                  "--spoke-delay": `${0.45 + (i / N) * 0.5}s`,
                } as CSSProperties
              }
            />
          ))}
          <text
            x={cx}
            y={cy + 4}
            textAnchor="middle"
            fontSize="30"
            fontWeight="300"
            fill="var(--text)"
          >
            {pctOf(segs[focus]).toFixed(0)}%
          </text>
          <text
            x={cx}
            y={cy + 22}
            textAnchor="middle"
            fontSize="7"
            letterSpacing="0.5"
            fill="var(--text)"
            className="tw-mono uppercase"
          >
            {segs[focus].name}
          </text>
        </svg>
      </div>
    </div>
  );
}
